import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { interval, Observable, of } from "rxjs";
import { map, shareReplay, startWith } from "rxjs/operators";
import { RaceDaysAllService } from '../service/raceDaysAll.service';
import { StreamerItem, StreamerService } from '../service/streamer.service';

interface timeComponents {
    secondsToDday: number;
    minutesToDday: number;
    hoursToDday: number;
    daysToDday: number;
}

/**
 * Next upcoming event from api/nextRaceDay.php – a planned race day, a
 * qualification session ("Shootout", may still be draft) or a published season
 * event. Season events can pin their own streamer.
 */
interface NextEvent {
    event_type?: 'raceday' | 'qualification' | 'event';
    event_date?: string;
    event_title?: string;
    circuit_name?: string;
    season_name?: string;
    status?: string;
    day_planned?: string;
    streamer_name?: string | null;
    streamer_url?: string | null;
}

/** Events start at 21:00 (no time column in the DB). */
const EVENT_START_HOUR = 21;

const WEEKDAYS_DE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

@Component({
    selector: 'app-nextRaceDay-widget',
    standalone: true,
    templateUrl: './nextRaceDay.component.html',
    styleUrls: ['./nextRaceDay.component.scss'],
    imports: [ 
        CommonModule,
    ],
    providers: [
        RaceDaysAllService,
        StreamerService,
    ]
})
export class NextRaceDayWidgetComponent implements OnInit {

    /** Fallback channel used while no main streamer is configured. */
    private static readonly FALLBACK_STREAM_URL = 'https://www.twitch.tv/simracenick';

    public timeLeft?: Observable<timeComponents>;
    public mainStreamer: StreamerItem | null = null;
    public nextEvent: NextEvent | null = null;
    public eventDate: Date | null = null;

    constructor(
        private raceDaysAllService: RaceDaysAllService,
        private streamerService: StreamerService,
    ) {
        // Initialize timeLeft with a default value
        const initialTimeLeft: timeComponents = {
            secondsToDday: 0,
            minutesToDday: 0,
            hoursToDday: 0,
            daysToDday: 0
        };
        this.timeLeft = of(initialTimeLeft);
    }

    ngOnInit() {
        this.streamerService.getMain().subscribe({
            next: (streamer) => this.mainStreamer = streamer,
            error: (error) => console.error('Error fetching main streamer:', error),
        });

        try {
            this.raceDaysAllService.getNextRaceDay().subscribe((day) => {
                const event: NextEvent | undefined = day?.[0];
                const myday = this.parseEventDate(event?.event_date ?? event?.day_planned);

                if ( event && myday !== null ) {
                    this.nextEvent = event;
                    this.eventDate = myday;

                    this.timeLeft = interval(1000).pipe(
                        startWith(0),
                        map(() => {
                            const timeDiff = this.calcDateDiff(myday);
                            // If the date is in the past, return zeroed-out time components
                            if (timeDiff.daysToDday < 0 || timeDiff.hoursToDday < 0 || timeDiff.minutesToDday < 0 || timeDiff.secondsToDday < 0) {
                                return { secondsToDday: 0, minutesToDday: 0, hoursToDday: 0, daysToDday: 0 };
                            }
                            return timeDiff;
                        }),
                        shareReplay(1)
                    );
                }
            });
        } catch (error) {
            console.error('Error fetching next race day:', error);
        }
    }

    /**
     * `YYYY-MM-DD` (optionally with a time part) as a local date at the event's
     * start hour – `new Date('YYYY-MM-DD')` would be parsed as UTC midnight and
     * shift the displayed day for viewers west of UTC.
     */
    private parseEventDate(value?: string): Date | null {
        const match = /^(\d{4})-(\d{2})-(\d{2})/.exec((value ?? '').trim());
        if (!match) {
            return null;
        }
        return new Date(+match[1], +match[2] - 1, +match[3], EVENT_START_HOUR, 0, 0, 0);
    }

    get isQualification(): boolean {
        return this.nextEvent?.event_type === 'qualification';
    }

    /** Headline above the countdown, naming the kind of event. */
    get eventHeadline(): string {
        // Same text for both event types, but could be different in the future.
        return this.isQualification ? 'Nächstes Liga Event in' : 'Nächstes Liga Event in';
    }

    /** e.g. "Sonntag, 09.08.2026 · 21:00 Uhr" */
    get eventDateLabel(): string {
        if (!this.eventDate) {
            return '';
        }
        const day = String(this.eventDate.getDate()).padStart(2, '0');
        const month = String(this.eventDate.getMonth() + 1).padStart(2, '0');
        return `${WEEKDAYS_DE[this.eventDate.getDay()]}, ${day}.${month}.${this.eventDate.getFullYear()}`
            + ` · ${EVENT_START_HOUR}:00 Uhr`;
    }

    /** Title of the event, plus its circuit when that is extra information. */
    get eventSubtitle(): string {
        const title = this.nextEvent?.event_title?.trim() || this.nextEvent?.circuit_name?.trim() || '';
        const circuit = this.nextEvent?.circuit_name?.trim() ?? '';
        if (circuit && circuit !== title) {
            return `${title} · ${circuit}`;
        }
        return title;
    }

    calcDateDiff(endDay: Date = new Date(2022, 0, 1, 21, 0)): timeComponents {
        const dDay = endDay.valueOf();

        const milliSecondsInASecond = 1000;
        const hoursInADay = 24;
        const minutesInAnHour = 60;
        const secondsInAMinute = 60;

        const timeDifference = dDay - Date.now();

        const daysToDday = Math.floor(
        timeDifference /
            (milliSecondsInASecond * minutesInAnHour * secondsInAMinute * hoursInADay)
        );

        const hoursToDday = Math.floor(
        (timeDifference /
            (milliSecondsInASecond * minutesInAnHour * secondsInAMinute)) %
            hoursInADay
        );

        const minutesToDday = Math.floor(
        (timeDifference / (milliSecondsInASecond * minutesInAnHour)) %
            secondsInAMinute
        );

        const secondsToDday =
        Math.floor(timeDifference / milliSecondsInASecond) % secondsInAMinute;

        return { secondsToDday, minutesToDday, hoursToDday, daysToDday };
    }

    /**
     * A season event may pin its own streamer; otherwise the league's main
     * streamer is used, and the hard-coded channel as a last resort.
     */
    get streamUrl(): string {
        const url = this.nextEvent?.streamer_url?.trim() || this.mainStreamer?.url?.trim();
        if (!url) {
            return NextRaceDayWidgetComponent.FALLBACK_STREAM_URL;
        }
        return /^https?:\/\//i.test(url) ? url : `https://${url}`;
    }

    get streamHint(): string {
        const eventUrl = this.nextEvent?.streamer_url?.trim();
        const name = eventUrl ? this.nextEvent?.streamer_name?.trim() : this.mainStreamer?.name?.trim();
        return name ? `Stream von ${name} öffnen` : 'Stream öffnen';
    }

    openTwitch(): void {
        window.open(this.streamUrl, '_blank');
    }
}